import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { User, Mail, Phone, Save, Shield } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

const ProfileSettingsClean = ({ user, onUpdate }) => {
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [saving, setSaving] = useState(false);
  
  // Phone verification
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    
    // Check if phone changed
    if (phone !== user?.phone) {
      // Trigger phone verification
      setNewPhone(phone);
      setShowPhoneVerification(true);
      handleSendPhoneOtp(phone);
      return;
    }

    // Save without phone change
    await saveProfile(name, email, user?.phone);
  };

  const handleSendPhoneOtp = async (phoneNumber) => {
    setSendingOtp(true);
    try {
      await axios.post(`${API}/user/verify-phone-change`, {
        new_phone: phoneNumber
      }, { withCredentials: true });
      
      toast.success('Doğrulama kodu gönderildi');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kod gönderilemedi');
      setShowPhoneVerification(false);
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyPhone = async () => {
    const code = otp.join('');
    if (code.length !== 6) {
      toast.error('6 haneli kodu girin');
      return;
    }

    setVerifyingOtp(true);
    try {
      await axios.post(`${API}/user/confirm-phone-change`, {
        code: code
      }, { withCredentials: true });

      toast.success('Telefon numarası doğrulandı');
      setShowPhoneVerification(false);
      
      // Now save profile with new phone
      await saveProfile(name, email, newPhone);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Doğrulama başarısız');
    } finally {
      setVerifyingOtp(false);
    }
  };

  const saveProfile = async (nameVal, emailVal, phoneVal) => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/user/profile`, {
        name: nameVal,
        email: emailVal,
        phone: phoneVal
      }, { withCredentials: true });

      toast.success('Profil bilgileri güncellendi');
      
      if (onUpdate) {
        onUpdate(response.data.user);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Profil güncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-emerald-600" />
            Profil Bilgileri
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-4 max-w-md">
            {/* Name */}
            <div>
              <Label htmlFor="profile-name">Ad Soyad</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="profile-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="profile-email">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="profile-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            {/* Phone */}
            <div>
              <Label htmlFor="profile-phone">Telefon Numarası</Label>
              <div className="relative mt-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="profile-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="pl-10"
                  placeholder="05XX XXX XX XX"
                />
              </div>
              {phone !== user?.phone && (
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <Shield className="w-3 h-3" />
                  Telefon değişikliği SMS ile doğrulanacak
                </p>
              )}
            </div>

            <Button 
              type="submit" 
              disabled={saving}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Phone Verification Dialog */}
      <Dialog open={showPhoneVerification} onOpenChange={setShowPhoneVerification}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <Phone className="w-6 h-6 text-emerald-600" />
            </div>
            <DialogTitle className="text-center">Telefon Doğrulama</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-slate-600 text-center">
              {newPhone} numarasına gönderilen 6 haneli kodu girin
            </p>
            
            <div className="flex justify-center gap-2">
              {otp.map((digit, index) => (
                <Input
                  key={index}
                  type="text"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => {
                    const newOtp = [...otp];
                    newOtp[index] = e.target.value;
                    setOtp(newOtp);
                    
                    // Auto-advance
                    if (e.target.value && index < 5) {
                      document.getElementById(`otp-${index + 1}`)?.focus();
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Backspace' && !otp[index] && index > 0) {
                      document.getElementById(`otp-${index - 1}`)?.focus();
                    }
                  }}
                  id={`otp-${index}`}
                  className="w-10 h-12 text-center text-lg font-bold p-0"
                />
              ))}
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              onClick={handleVerifyPhone}
              disabled={verifyingOtp || otp.join('').length !== 6}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              {verifyingOtp ? 'Doğrulanıyor...' : 'Doğrula ve Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProfileSettingsClean;
